import { Dayjs } from 'dayjs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Shift } from '../../types/schedule.model'
import { tileBackground } from '../../utils/colors'
import {
  DAY_GRID_END_HOUR,
  DAY_GRID_START_HOUR,
  DEFAULT_VISIBLE_HOUR,
  MINUTES_IN_HOUR,
  crossesMidnight,
  formatMinutes,
  toDateKey
} from '../../utils/schedule'
import styles from './DayCalendar.module.scss'
import OverlayScrollbar from '@/shared/components/OverlayScrollbar/OverlayScrollbar'
import {
  DEFAULT_SHIFT_TYPE,
  shiftTypeDefinition
} from '../../types/shiftType.model'

interface DayCalendarProps {
  /** The day being shown */
  day: Dayjs
  /** Column per employee, in this order */
  employees: Array<{ userId: string; name: string }>
  /** Every shift on this day, across all employees */
  shifts: Shift[]
  employeeColor: (userId: string) => string
  /** Clicking an existing shift */
  onEditShift: (shift: Shift) => void
  /** Clicking an empty slot in an employee's column starts a new shift there */
  onAddShift: (userId: string, date: string, startMinutes: number) => void
  isLoading?: boolean
}

/**
 * How many shifts share a column's width before they start stacking instead of
 * dividing it further. One person rarely has more than two shifts in a day, so
 * this is a guard against unreadable slivers rather than a common case.
 */
const MAX_SIDE_BY_SIDE = 2

type PositionedShift = {
  shift: Shift
  /** Share of the column width, 0-1, and how far along it the block starts */
  width: number
  offset: number
}

/**
 * Lays out one employee's shifts for the day.
 *
 * Two shifts that overlap in time cannot share the same strip of column, so
 * they divide it. Overlap is decided against the running maximum end rather
 * than against the previous shift alone: a long opening shift overlaps both of
 * two short later ones even when those two do not touch each other.
 *
 * Within a group the lane is the first one that is actually free, not the
 * shift's position in the list. Indexing by position looked the same for two
 * shifts and then put the third exactly on top of the first, because
 * `index % lanes` sends 0 and 2 to the same place while both are still running.
 */
const position = (shifts: Shift[]): PositionedShift[] => {
  const ordered = [...shifts].sort((a, b) => a.startMinutes - b.startMinutes)
  const groups: Shift[][] = []
  let current: Shift[] = []
  let groupEnd = -1

  ordered.forEach(shift => {
    if (current.length && shift.startMinutes >= groupEnd) {
      groups.push(current)
      current = []
      groupEnd = -1
    }

    current.push(shift)
    groupEnd = Math.max(groupEnd, shift.endMinutes)
  })

  if (current.length) groups.push(current)

  return groups.flatMap(group => {
    const lanes = Math.min(group.length, MAX_SIDE_BY_SIDE)
    // When a lane frees up it is reused, so three shifts where the first has
    // already ended still read as two columns rather than three slivers.
    const laneEnds: number[] = []

    return group.map(shift => {
      let lane = laneEnds.findIndex(end => end <= shift.startMinutes)

      if (lane === -1) {
        lane = laneEnds.length < lanes ? laneEnds.length : 0
      }

      laneEnds[lane] = shift.endMinutes

      return {
        shift,
        width: 1 / lanes,
        offset: lane / lanes
      }
    })
  })
}

/**
 * The schedule's day view: the hour axis of the week calendar, but with a
 * column per EMPLOYEE instead of per day.
 *
 * That is the whole reason to look at a single day, who overlaps with whom.
 * Read the same day in the week view and the answer is spread across one
 * narrow column with everyone's blocks stacked into it.
 *
 * Click-only by design: dragging to create and to move belongs to the week
 * view, which owns a shift's day. Here every column is the same day, so a drag
 * across columns would mean reassigning the shift to another person, a
 * different and much more consequential gesture than nudging its hours.
 */
function DayCalendar(props: DayCalendarProps) {
  const {
    day,
    employees,
    shifts,
    employeeColor,
    onEditShift,
    onAddShift,
    isLoading
  } = props
  const { t } = useTranslation()

  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const skeletonScrollerRef = useRef<HTMLDivElement | null>(null)
  const headerRef = useRef<HTMLDivElement | null>(null)

  /*
    The overlay bar starts below the header, as it does in the week calendar.
    There the header sits outside the scroller and the bar simply spans what is
    left; here it cannot, because this grid scrolls sideways too and splitting
    the two apart is what let the day columns drift from the names above them.
    So the header stays sticky inside the scroller and the bar is pushed down
    past it by its measured height, which is set by its text rather than by a
    number anything could hard-code.
  */
  const [headerHeight, setHeaderHeight] = useState(0)

  useEffect(() => {
    const node = headerRef.current
    if (!node) return undefined

    const measure = () => setHeaderHeight(node.getBoundingClientRect().height)

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)

    return () => observer.disconnect()
  }, [])

  const hours = useMemo(
    () =>
      Array.from(
        { length: DAY_GRID_END_HOUR - DAY_GRID_START_HOUR },
        (_, index) => DAY_GRID_START_HOUR + index
      ),
    []
  )

  const gridStartMinutes = DAY_GRID_START_HOUR * MINUTES_IN_HOUR
  const gridEndMinutes = DAY_GRID_END_HOUR * MINUTES_IN_HOUR
  // Geometry is a share of the visible window, not a fixed px per hour, so the
  // calendar fills whatever height the card has instead of overflowing it.
  const windowMinutes = gridEndMinutes - gridStartMinutes

  const toPercent = useCallback(
    (minutes: number) => ((minutes - gridStartMinutes) / windowMinutes) * 100,
    [gridStartMinutes, windowMinutes]
  )

  const dateKey = toDateKey(day)

  // Opens where the day starts being interesting rather than on empty night.
  // Once per day, re-running would fight the user's own scrolling, and keyed
  // on `isLoading` too, because the scroller only exists in the loaded branch.
  const scrolledDayRef = useRef<string | null>(null)

  useEffect(() => {
    const element = scrollerRef.current
    if (!element || scrolledDayRef.current === dateKey) return

    scrolledDayRef.current = dateKey
    element.scrollTop =
      ((DEFAULT_VISIBLE_HOUR * MINUTES_IN_HOUR) / windowMinutes) *
      element.scrollHeight
  }, [dateKey, windowMinutes, isLoading])

  const shiftsByUser = useMemo(() => {
    const map = new Map<string, Shift[]>()
    shifts.forEach(shift => {
      const existing = map.get(shift.userId)
      if (existing) {
        existing.push(shift)
      } else {
        map.set(shift.userId, [shift])
      }
    })
    return map
  }, [shifts])

  // The gutter plus one track per employee, with a floor so a busy day scrolls
  // sideways rather than shrinking every name to an ellipsis.
  const columnTemplate = `3.5rem repeat(${Math.max(
    employees.length,
    1
  )}, minmax(7rem, 1fr))`

  if (!employees.length) {
    return (
      <div className={styles.calendar}>
        <p className={`body-medium ${styles.empty}`}>
          {t('SCHEDULE.NO_EMPLOYEES')}
        </p>
      </div>
    )
  }

  const header = (
    <>
      <div className={styles.corner} ref={headerRef} />
      {employees.map(employee => (
        <div
          key={employee.userId}
          className={`body-bold-medium ${styles.employeeHeader}`}
          title={employee.name}
        >
          {employee.name}
        </div>
      ))}
    </>
  )

  const gutter = (
    <div className={styles.hourGutter}>
      {hours.map(hour => (
        <div key={hour} className={`body-small ${styles.hourLabel}`}>
          {`${String(hour).padStart(2, '0')}:00`}
        </div>
      ))}
    </div>
  )

  // The real calendar minus its shifts: headers, hour gutter, column geometry
  // and the scrollbar are all the loaded ones, so nothing moves when the data
  // arrives. No placeholder blocks, invented grey shifts read as real ones.
  if (isLoading) {
    return (
      <div className={styles.calendar}>
        <div className={styles.scrollbarLane} style={{ top: headerHeight }}>
          <OverlayScrollbar targetRef={skeletonScrollerRef} />
        </div>
        <div className={styles.scroller} ref={skeletonScrollerRef}>
          <div
            className={styles.grid}
            style={{ gridTemplateColumns: columnTemplate }}
          >
            {header}
            {gutter}
            {employees.map(employee => (
              <div key={employee.userId} className={styles.employeeColumn}>
                {hours.map(hour => (
                  <div key={hour} className={styles.hourSlotStatic} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.calendar}>
      <div className={styles.scrollbarLane} style={{ top: headerHeight }}>
        <OverlayScrollbar targetRef={scrollerRef} />
      </div>
      <div className={styles.scroller} ref={scrollerRef}>
        <div
          className={styles.grid}
          style={{ gridTemplateColumns: columnTemplate }}
        >
          {header}
          {gutter}

          {employees.map(employee => (
            <div key={employee.userId} className={styles.employeeColumn}>
              {hours.map(hour => (
                <button
                  type="button"
                  key={hour}
                  className={styles.hourSlot}
                  onClick={() =>
                    onAddShift(employee.userId, dateKey, hour * MINUTES_IN_HOUR)
                  }
                  aria-label={t('SCHEDULE.DAY.ADD_AT', {
                    name: employee.name,
                    time: formatMinutes(hour * MINUTES_IN_HOUR)
                  })}
                />
              ))}

              {position(shiftsByUser.get(employee.userId) ?? []).map(
                ({ shift, width, offset }) => {
                  const color = employeeColor(shift.userId)
                  // A shift running past midnight is stored past 1440
                  // (16:00→02:00 is 960→1560). Its tail belongs to the next
                  // day, so the block stops at midnight here rather than
                  // running off the bottom of the column; the times in the
                  // label still say where it really ends.
                  const drawnEnd = Math.min(shift.endMinutes, gridEndMinutes)
                  const definition = shiftTypeDefinition(shift.type)
                  // An all-day entry is stored as 00:00-24:00, which as a
                  // caption says nothing. It names itself instead.
                  const label = definition.allDay
                    ? t(definition.labelKey)
                    : `${formatMinutes(
                        shift.startMinutes
                      )} – ${formatMinutes(shift.endMinutes)}`
                  /*
                    Named on the block, otherwise a dyzur or a szkolenie is a
                    plain time range and reads as an ordinary shift. Skipped for
                    working time, which is the default on nearly every block and
                    would be noise, and for an all-day entry, whose name is
                    already the caption above.
                  */
                  const typeLabel =
                    definition.allDay || definition.type === DEFAULT_SHIFT_TYPE
                      ? null
                      : t(definition.labelKey)

                  return (
                    <button
                      type="button"
                      key={shift.id}
                      className={styles.shiftBlock}
                      onClick={() => onEditShift(shift)}
                      // The block truncates, so the tooltip carries the whole
                      // of it: what it is, when, and the note in full.
                      title={[
                        crossesMidnight(shift)
                          ? `${label} (${t('SCHEDULE.OVERNIGHT')})`
                          : label,
                        typeLabel,
                        shift.note
                      ]
                        .filter(Boolean)
                        .join(' - ')}
                      style={{
                        top: `${toPercent(shift.startMinutes)}%`,
                        height: `calc(${
                          toPercent(drawnEnd) - toPercent(shift.startMinutes)
                        }% - 2px)`,
                        left: `${offset * 100}%`,
                        width: `${width * 100}%`,
                        borderLeftColor: color,
                        background: tileBackground(color)
                      }}
                    >
                      <span className={`body-small ${styles.shiftTime}`}>
                        {label}
                      </span>
                      {typeLabel && (
                        <span className={`body-small ${styles.shiftType}`}>
                          {typeLabel}
                        </span>
                      )}
                      {shift.note && (
                        <span className={`body-small ${styles.shiftNote}`}>
                          {shift.note}
                        </span>
                      )}
                    </button>
                  )
                }
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default DayCalendar
